import 'twin.macro';
import 'styled-components/macro';
import { Link, useParams } from 'react-router-dom';

import { useRatingsContext } from 'toy/context/Ratings';
import { useShoppingCartContext } from 'toy/context/ShoppingCart';
import { ReviewCard } from 'toy/molecules/review-card/ReviewCard';
import { ReviewForm } from 'toy/molecules/review-form/ReviewForm';
import { useSweetsQuery } from 'types/graphql-toy';

import { ButtonV2 } from '../../../component-lib/atoms/button-v2/ButtonV2';
import { Icon } from '../../../component-lib/atoms/icon/Icon';
import { EmptyPlaceholder } from '../../../component-lib/molecules/placeholder/EmptyPlaceholder';
import { LoadingPlaceholder } from '../../../component-lib/molecules/placeholder/LoadingPlaceholder';
import { datetimeFormat, shortEnglishHumanizer } from '../../../support/dates';
import { BackButton } from '../../atoms/back-button/BackButton';
import { SmartFavoritiesToggle } from '../../molecules/smart-favorites-toggle/SmartFavoritiesToggle';

export const Details = () => {
  const params = useParams<{ id: string }>();
  const { loading, data: sweetsData } = useSweetsQuery();
  const { addToCart } = useShoppingCartContext();
  const { addReview, getReviews } = useRatingsContext();

  if (loading) {
    return <LoadingPlaceholder height={300} />;
  }

  const theSweet = sweetsData?.sweets?.find(sweet => sweet.id === params.id);

  if (!theSweet) {
    return (
      <EmptyPlaceholder
        title="There is not such candy!"
        description="Something went wrong! Try another candy!"
        buttons={
          <Link to="/toy-shop/sweet">
            <ButtonV2 variant="primary">Go to all sweets</ButtonV2>
          </Link>
        }
      />
    );
  }

  const expirationDate = new Date(theSweet.expires_at);
  const dateNow = Date.now();
  const isExpired = expirationDate.getTime() < dateNow;
  const expirationPeriod = shortEnglishHumanizer(dateNow - expirationDate.getTime());

  return (
    <div>
      <div tw="flex justify-between">
        <div tw="mb-2 flex items-center">
          <h1 tw="display-1 mr-1">{theSweet.title}</h1>
          <div tw="text-primary-light mr-2">({theSweet.id})</div>
          <SmartFavoritiesToggle id={theSweet.id} />
        </div>

        <BackButton />
      </div>
      {isExpired ? (
        <div tw="text-error text-3xl mb-2">
          <Icon name="circle-remove" tw="mr-half" aria-label="warning" /> Expired!
          <div tw="text-lg">Date of expiration: {datetimeFormat(expirationDate)}</div>
        </div>
      ) : (
        <div tw="text-green text-3xl mb-2">
          <Icon name="circle-check" tw="mr-half" aria-label="success" /> Fresh!
          <div tw="text-lg">{expirationPeriod} until expiration</div>
        </div>
      )}
      Created: {datetimeFormat(new Date(theSweet.created_at))}
      <ButtonV2
        tw="block mt-2"
        variant="primary"
        onClick={() => addToCart({ id: theSweet.id, title: theSweet.title, expiresAt: theSweet.expires_at })}
      >
        Add to cart
      </ButtonV2>
      <div tw="flex flex-col gap-3 mt-[80px]">
        <h1 tw="text-3xl">Reviews</h1>

        <ReviewForm
          onSubmit={review => {
            addReview({ sweetId: params.id, ...review, rating: review.rating || 0 });
          }}
        />

        <div tw="flex flex-col gap-2">
          {getReviews(params.id).map(review => {
            return <ReviewCard author={review.author} rating={review.rating} text={review.text} />;
          })}
        </div>
      </div>
    </div>
  );
};
